import React from "react";
import { Logo } from "./components/Logo";
import { SEO } from "./components/SEO.tsx";
import { useStore } from "./store/useStore";
import { motion } from "motion/react";

const Header = React.lazy(() =>
  import("./components/Header").then((mod) => ({ default: mod.Header }))
);
const Sidebar = React.lazy(() =>
  import("./components/Sidebar").then((mod) => ({ default: mod.Sidebar }))
);
const RequestPanel = React.lazy(() =>
  import("./components/RequestPanel").then((mod) => ({ default: mod.RequestPanel }))
);
const ResponsePanel = React.lazy(() =>
  import("./components/ResponsePanel").then((mod) => ({ default: mod.ResponsePanel }))
);

export default function App() {
  const { init, isInitialized } = useStore();
  const [requestPanelHeight, setRequestPanelHeight] = React.useState(60); // percentage
  const isResizing = React.useRef(false);

  React.useEffect(() => {
    init();
  }, [init]);

  const startResizing = React.useCallback((e: React.MouseEvent) => {
    isResizing.current = true;
    document.addEventListener("mousemove", handleResizing);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "row-resize";
  }, []);

  const stopResizing = React.useCallback(() => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleResizing);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "default";
  }, []);

  const handleResizing = React.useCallback((e: MouseEvent) => {
    if (!isResizing.current) return;
    const containerHeight = window.innerHeight - 56 - 32 - 24; // header - footer - padding
    const newHeight = ((e.clientY - 56 - 12) / containerHeight) * 100;
    if (newHeight > 20 && newHeight < 80) {
      setRequestPanelHeight(newHeight);
    }
  }, []);

  if (!isInitialized) {
    return (
      <div className="h-screen w-screen bg-navy-main flex flex-col items-center justify-center text-text-main">
        <Logo size={120} className="mb-12 flex-col gap-6" />
        <div className="flex flex-col items-center gap-2">
          <div className="w-48 h-1 bg-[#1A1A1A] rounded-full overflow-hidden">
             <motion.div 
               initial={{ x: "-100%" }}
               animate={{ x: "0%" }}
               transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
               className="w-full h-full bg-primary"
             />
          </div>
          <p className="text-[10px] font-black text-text-muted uppercase tracking-[0.3em] animate-pulse">Initializing Setup</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-navy-main text-text-main font-sans selection:bg-primary selection:text-white">
      <SEO />
      <React.Suspense fallback={<div className="h-14 shrink-0 bg-surface-panel border-b border-navy-border" />}>
        <Header />
      </React.Suspense>
      
      <main className="flex-1 flex overflow-hidden p-3 gap-3">
        <React.Suspense fallback={<div className="w-64 rounded-xl border border-navy-border bg-surface-panel" />}>
          <Sidebar />
        </React.Suspense>
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <div 
            style={{ height: `${requestPanelHeight}%` }}
            className="flex flex-col min-h-0 bg-surface-panel rounded-xl border border-navy-border shadow-xl overflow-hidden"
          >
            <React.Suspense fallback={<div className="h-full w-full" />}>
              <RequestPanel />
            </React.Suspense>
          </div>
          
          {/* Draggable Divider */}
          <div 
            onMouseDown={startResizing}
            className="h-3 w-full flex items-center justify-center cursor-row-resize group relative z-10"
          >
            <div className="w-12 h-1 bg-navy-border rounded-full group-hover:bg-primary/50 transition-premium"></div>
            <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-premium"></div>
          </div>
          
          <div 
            style={{ height: `${100 - requestPanelHeight}%` }}
            className="bg-surface-panel rounded-xl border border-navy-border shadow-xl overflow-hidden"
          >
            <React.Suspense fallback={<div className="h-full w-full" />}>
              <ResponsePanel />
            </React.Suspense>
          </div>
        </div>
      </main>

      {/* Status Bar */}
      <footer className="h-8 bg-surface-panel border-t border-navy-border text-text-muted flex items-center justify-between px-4 text-[10px] font-bold uppercase tracking-widest shrink-0">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_8px_rgba(34,197,94,0.5)] animate-pulse"></div>
            <span>System Online</span>
          </div>
          <div className="h-3 w-[1px] bg-navy-border"></div>
          <span className="opacity-70">Storage: IndexedDB (Local-first)</span>
        </div>
        <div className="flex items-center gap-4 opacity-70">
          <span>UTF-8</span>
          <span>JSON</span>
          <span>Ln 1, Col 1</span>
        </div>
      </footer>
    </div>
  );
}
